import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ApiServices, { API_URL } from '../../config/ApiServices';
import { toast } from 'react-toastify';
import { hideLoader, showLoader } from '../../redux/actions/LoaderActions';
import Loader from '../../utils/Loader';
import { showModal } from '../../redux/actions/ModalActions';
import AttachmentIcon from '@mui/icons-material/Attachment';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Box,
    Divider,
    Chip,
    Stack,
    Grid,
} from '@mui/material';
import ModalManager from '../../Modals/ModalManager';

export default function TaskDetails() {
    
    const { taskId, studentId } = useParams();

    const dispatch = useDispatch();
    const loading = useSelector((state) => state.loader.loading);
    const isOpen = useSelector((state) => state.modal.isOpen);

    const [taskDetails, setTaskDetails] = useState(null);
    const [attachments, setAttachments] = useState([]);
    const [marksOb, setMarksOb] = useState(undefined);

    const getAttachmentName = (filename) => {
        let parts = filename?.split('-');
        return parts?.length > 1 ? parts.slice(1).join('-') : filename;
    };

    // helper: check if file is an image
    const isImageFile = (url) => {
        return (/\.(jpg|jpeg|png|gif|bmp|webp)$/i).test(url);
    };

    useEffect(() => {
        if (!isOpen) {
            dispatch(showLoader());

            // Fetch task details (Trainer attachments + trainer images are inside this)
            ApiServices.apiRequest("getSingleTask", { _id: taskId })
                .then((res) => {
                    if (res.data.success) {
                        setTaskDetails(res.data.data);
                    } else {
                        toast.error(res.data.message);
                    }
                })
                .catch((err) => {
                    toast.error(err?.message ? err?.message : "Something went wrong");
                });

            // Fetch task attachments of that student
            ApiServices.apiRequest("getUploadedTask", { studentId: studentId, taskId: taskId })
                .then((res) => {
                    if (res.data.success) {
                        setAttachments(res.data.data[0]?.attachments || []);
                    } else {
                        toast.error(res.data.message);
                    }
                })
                .catch((err) => {
                    toast.error(err?.message ? err?.message : "Something went wrong");
                });

            // Fetch marks of that student
            ApiServices.apiRequest("getMarks", { studentId: studentId, testId: taskId })
                .then((res) => {
                    if (res.data.success) {
                        setMarksOb(res.data.data[0]?.score);
                    } else {
                        toast.error(res.data.message);
                    }
                })
                .catch((err) => {
                    toast.error(err?.message ? err?.message : "Something went wrong");
                })
                .finally(() => {
                    dispatch(hideLoader());
                });
        }
    }, [taskId, studentId, dispatch, isOpen]);

    return (
        <>
            <div className="container">
                <div className="page-inner">
                    {loading ? (
                        <Loader />
                    ) : (
                        <Card>
                            <CardContent>
                                <Typography variant="h4" gutterBottom>
                                    Task Details
                                </Typography>
                                {taskDetails ? (
                                    <Box>
                                        <Typography variant="h5" component="div">
                                            {taskDetails.name}
                                        </Typography>
                                        <Typography color="textSecondary">
                                            Due Date: {taskDetails.date}
                                        </Typography>
                                        <Typography color="textSecondary">
                                            Maximum Marks: {taskDetails.maximumMarks}
                                        </Typography>
                                        {marksOb !== undefined && (
                                            <Typography color="textSecondary">
                                                Obtained Marks: {marksOb}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" component="p" sx={{ mb: 2 }}>
                                            {taskDetails.description}
                                        </Typography>

                                        {/* ---------------- Trainer Section ---------------- */}
                                        <Divider sx={{ my: 2 }}>
                                            <Chip label="Trainer Attachments" color="primary" />
                                        </Divider>

                                        {/* Trainer Images OR Files */}
                                        {taskDetails.images?.length > 0 ? (
                                            <Grid container spacing={2}>
                                                {taskDetails.images.map((fileUrl, index) => (
                                                    <Grid item xs={6} sm={4} md={3} key={index}>
                                                        {isImageFile(fileUrl) ? (
                                                            <a
                                                                href={fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src={fileUrl}
                                                                    alt={`Trainer Attachment ${index + 1}`}
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "150px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "8px",
                                                                        boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
                                                                    }}
                                                                />
                                                            </a>
                                                        ) : (
                                                            <Link
                                                                to={`${fileUrl}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="d-flex align-items-center text-decoration-none"
                                                            >
                                                                <AttachmentIcon sx={{ color: 'grey' }} />
                                                                <Typography
                                                                    variant="body2"
                                                                    component="span"
                                                                    className="ms-2"
                                                                >
                                                                    {getAttachmentName(fileUrl)}
                                                                </Typography>
                                                            </Link>
                                                        )}
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        ) : (
                                            <Typography color="textSecondary" sx={{ mt: 1 }}>
                                                No trainer images provided.
                                            </Typography>
                                        )}

                                        {/* ---------------- Student Section ---------------- */}
                                        <Divider sx={{ my: 3 }}>
                                            <Chip label="Student Attachments" color="secondary" />
                                        </Divider>

                                        {attachments?.length > 0 ? (
                                            <Stack spacing={1}>
                                                {attachments.map((file, index) => (
                                                    <Link
                                                        key={index}
                                                        to={`${file}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="d-flex align-items-center text-decoration-none"
                                                    >
                                                        <AttachmentIcon sx={{ color: 'grey' }} />
                                                        <Typography
                                                            variant="body2"
                                                            component="span"
                                                            className="ms-2"
                                                        >
                                                            {getAttachmentName(file)}
                                                        </Typography>
                                                    </Link>
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography color="textSecondary">
                                                No attachments available.
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography color="textSecondary">
                                        Loading task details...
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </>
    );
}
